<script setup lang="ts">
  import { tagTypeUniqeId, type UserTagType } from "~/entities/UserTagType";
  import { useDeliveryStore } from "~/stores/DeliveryStore";

  const localProps = defineProps<{
    title: string;
    userTagTypes: UserTagType[];
    value: UserTagType[];
  }>();

  const emits = defineEmits(["update:value"]);

  const deliveryStore = useDeliveryStore();

  const localValue = ref<string[]>(localProps.value.map(tag => tagTypeUniqeId(tag)));

  const handleClick = (tag: UserTagType) => {
    if (localValue.value.includes(tagTypeUniqeId(tag))) {
      localValue.value = localValue.value.filter(id => id !== tagTypeUniqeId(tag));
    } else {
      localValue.value = [...localValue.value, tagTypeUniqeId(tag)];
    }
  };

  const handleConfirm = () => {
    const selectedTagTypes = localProps.userTagTypes.filter(tag => localValue.value.includes(tagTypeUniqeId(tag)));
    emits("update:value", selectedTagTypes);
    menu.value = false;
  };

  const isSelected = (tag: UserTagType) => {
    return localValue.value.includes(tagTypeUniqeId(tag));
  };

  watch(
    () => localProps.value,
    value => {
      localValue.value = value.map(tag => tagTypeUniqeId(tag));
    },
  );

  const accountPlans = computed(() => deliveryStore.accountPlans);

  const tagsByPlan = computed(() => {
    const result: Record<string, any> = {};

    accountPlans.value.forEach(plan => {
      result[plan.planId] = {
        planTitle: plan.planTitle,
        planId: plan.planId,
        tags: [],
      };
    });

    localProps.userTagTypes.forEach(tag => {
      const planId = tag.userTagKey1;
      if (result[planId]) {
        result[planId].tags.push(tag);
      }
    });
    return Object.fromEntries(Object.entries(result).filter(([_, planData]) => (planData as any).tags.length > 0));
  });

  const menu = ref(false);
</script>

<template>
  <v-menu v-model="menu" :close-on-content-click="false" max-width="400">
    <template #activator="{ props }">
      <AtomsButton
        v-bind="props"
        class="mb-2"
        variant="outlined"
        size="x-large"
        color="white"
        data-testid="activator-btn"
      >
        {{ title }}
      </AtomsButton>
    </template>
    <div class="tags-menu">
      <div v-if="Object.keys(tagsByPlan).length > 0" class="contents">
        <div v-for="(planData, planId) in tagsByPlan" :key="planId" class="plan-section">
          <div class="plan-header mb-1">
            <span class="plan-title">{{ planData.planTitle }}</span>
            <span class="tag-count ms-2">({{ planData.tags.length }})</span>
          </div>

          <div class="d-flex ga-2 flex-wrap">
            <v-chip
              v-for="(tag, i) in planData.tags"
              :key="i"
              class="multiline"
              :variant="isSelected(tag) ? 'flat' : 'outlined'"
              :ripple="false"
              link
              :color="isSelected(tag) ? 'primary' : 'white'"
              @click="handleClick(tag)"
            >
              {{ tag.userTagName }}
            </v-chip>
          </div>

          <v-divider class="my-2" color="rgba(255, 255, 255, 0.12)" />
        </div>
      </div>
      <div
        v-else-if="Object.keys(tagsByPlan).length == 0 && userTagTypes.length > 0"
        class="contents d-flex ga-2 flex-wrap py-2"
      >
        <v-chip
          v-for="(tag, i) in userTagTypes"
          :key="i"
          class="chip-multiline"
          :variant="isSelected(tag) ? 'flat' : 'outlined'"
          :ripple="false"
          link
          :color="isSelected(tag) ? 'primary' : 'white'"
          @click="handleClick(tag)"
        >
          {{ tag.userTagName }}
        </v-chip>
      </div>
      <div v-else class="text-white text-subtitle text-center pt-3">タグはありません。</div>

      <div>
        <AtomsButton :disabled="userTagTypes.length == 0" class="mt-2" color="primary" @click="handleConfirm">
          追加する
        </AtomsButton>
      </div>
    </div>
  </v-menu>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .tags-menu {
    background-color: rgba(var(--v-theme-monochrome-06), 1);
    border-radius: 5px;
    padding: 8px;
    max-height: 70vh;
    overflow-y: auto;
    scrollbar-width: none;
  }
  .contents {
    max-height: calc(70vh - 70px);
    overflow-y: auto;
    scrollbar-width: none;
  }
  .chip-multiline {
    min-height: 32px;
    height: auto;
    white-space: normal;
  }
  .plan-header {
    padding: 4px 4px;
    border-radius: 4px;
  }
  .plan-title {
    font-weight: 500;
    color: white;
  }
  .tag-count {
    font-size: 0.85em;
    color: rgba(255, 255, 255, 0.7);
  }
</style>
